import { type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import {
  usePrefsStore,
  useSavedPlansStore,
  exportAll,
  importAll,
  clearAll,
  clearProgress,
  loadAllReviews,
  type ExportPayload,
} from "@music-app/persistence";
import { useCardsStore } from "@music-app/persistence";
import {
  createMidiInput,
  type MidiDeviceInfo,
  type MidiInput,
} from "@music-app/input-midi";
import { useAuthStore, useSyncStore, isConfigured, clearUserData, clearUserProgress } from "@music-app/sync-supabase";
import {
  optimizeWeights,
  MASTERY_PRESETS,
  APP_PLANS,
  resolveActivePlan,
  forecastDue,
  getItemTrack,
  type CardStore,
  type Track,
} from "@music-app/learning-system";

export function SettingsPage(): ReactNode {
  const prefs = usePrefsStore();
  const userPlans = useSavedPlansStore((s) => s.plans);
  const cards = useCardsStore((s) => s.cards);
  const hydrateCards = useCardsStore((s) => s.hydrate);
  const importInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleExport = async () => {
    const payload = await exportAll();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `earwise-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async (payload: ExportPayload) => {
    try {
      await importAll(payload);
      // Re-hydrate both stores to pick up imported state. Cards live in
      // useCardsStore; preferences live in usePrefsStore. Without both,
      // the UI stays stuck on whatever was in memory at import time.
      const cardsStore = useCardsStore.getState();
      cardsStore.replaceAll({});
      await Promise.all([hydrateCards(), usePrefsStore.getState().hydrate()]);
      setImportStatus({ kind: "success" });
    } catch (err) {
      setImportStatus({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    } finally {
      setPendingImportPayload(null);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImportStatus(null);
    const text = await file.text();
    try {
      const payload = JSON.parse(text) as ExportPayload;
      if (auth.user) {
        setPendingImportPayload(payload);
      } else {
        await doImport(payload);
      }
    } catch (err) {
      setImportStatus({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  };

  const handleClear = () => {
    setClearError(null);
    setClearConfirmPending(true);
  };

  const handleClearConfirm = async () => {
    setClearConfirmPending(false);
    const isSignedIn = !!auth.user;
    if (isSignedIn) {
      const { error } = await clearUserData();
      if (error) {
        setClearError(`Failed to clear cloud data: ${error}`);
        return;
      }
    }
    await clearAll();
    useCardsStore.getState().replaceAll({});
    location.reload();
  };

  /**
   * Vocal-mode toggle handler. Turning it ON calls getUserMedia
   * immediately so the browser surfaces its permission prompt during
   * the user gesture (toggle click) — promising it later inside the
   * Practice "Sing the answer" button would also work, but doing it
   * here gets the friction out of the way before the user is mid-flow.
   *
   * On grant: persist the pref and stop the test stream.
   * On denial: revert the toggle and surface a one-time explainer.
   *   We never auto-prompt again from this code path; the user has
   *   to flip the toggle themselves to retry.
   * Turning it OFF: just persist false; nothing to clean up because
   *   the actual PitchDetector is owned by /practice's route, not here.
   */
  const handleVocalModeToggle = async (next: boolean) => {
    if (!next) {
      await prefs.setVocalModeEnabled(false);
      setVocalModeError(null);
      setMicLabel(null);
      return;
    }
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setVocalModeError(
        "This browser doesn't expose a microphone API. Try Chrome, Firefox, or Safari on a recent version.",
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      // Read out the device label from the live track so the UI can
      // confirm which mic the browser handed us. enumerateDevices()
      // labels are populated only after permission is granted, so
      // this is the first place the label is available.
      const track = stream.getAudioTracks()[0];
      const label = track?.label ?? null;
      // Stop the probe stream — Practice will request its own when the
      // user clicks "Sing the answer". Holding it here would leave the
      // browser's mic indicator on for no reason.
      for (const t of stream.getTracks()) t.stop();
      setMicLabel(label && label.length > 0 ? label : "Default microphone");
      setVocalModeError(null);
      await prefs.setVocalModeEnabled(true);
    } catch (err) {
      const name = (err as { name?: string }).name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        setVocalModeError(
          "Microphone access was blocked. Open your browser's site-permissions panel for this page and allow the microphone, then re-toggle this option.",
        );
      } else {
        setVocalModeError(
          `Couldn't access the microphone (${name ?? "unknown error"}). Check that no other app is using it and try again.`,
        );
      }
      // Make sure the pref stays off; revert any optimistic UI flip.
      await prefs.setVocalModeEnabled(false);
    }
  };

  // Mic device label and one-time explainer text. State is per-mount so
  // navigating away clears the messaging — the persisted truth is `prefs.vocalModeEnabled`.
  const [micLabel, setMicLabel] = useState<string | null>(null);
  const [vocalModeError, setVocalModeError] = useState<string | null>(null);

  // In-page feedback replacements for browser alert()/confirm() (P45)
  const [importStatus, setImportStatus] = useState<null | { kind: "success" } | { kind: "error"; message: string }>(null);
  const [pendingImportPayload, setPendingImportPayload] = useState<ExportPayload | null>(null);
  const [clearConfirmPending, setClearConfirmPending] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [deleteConfirmPending, setDeleteConfirmPending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [placementConfirmPending, setPlacementConfirmPending] = useState(false);
  const [placementClearError, setPlacementClearError] = useState<string | null>(null);

  /* ─── MIDI input mode (Phase 2A C.2) ─────────────────────────────── */
  // Per-page MidiInput instance so the device picker can react to
  // hot-plug without Practice being open. Mirrors the per-mount
  // PitchDetector pattern from C.1: lazy init on the user gesture
  // (toggle ON), destroyed on unmount.
  const midiRef = useRef<MidiInput | null>(null);
  const [midiDevices, setMidiDevices] = useState<MidiDeviceInfo[]>([]);
  const [midiError, setMidiError] = useState<string | null>(null);
  /** True when the browser doesn't ship Web MIDI at all (Safari < 18,
   *  Firefox < 108, anywhere outside a browser). */
  const [midiUnsupported, setMidiUnsupported] = useState(false);

  // Tear down the per-page MidiInput on unmount. Practice runs its own
  // instance — we don't share, since both can outlive the other.
  useEffect(() => {
    return () => {
      midiRef.current?.destroy();
      midiRef.current = null;
    };
  }, []);

  // When MIDI mode is already enabled at mount (persisted), spin up the
  // MidiInput so the device picker has a live source. requestMIDIAccess
  // doesn't re-prompt once permission was granted — reading state on a
  // page that already has MIDI permission is silent.
  useEffect(() => {
    if (!prefs.midiInputEnabled) return;
    if (midiRef.current) return;
    let cancelled = false;
    void (async () => {
      const m = createMidiInput();
      try {
        await m.init();
      } catch {
        // Permission may have been revoked since the toggle was last
        // flipped on. Don't auto-flip the toggle off — let the user
        // see the device picker as empty + (eventually) the explainer.
        return;
      }
      if (cancelled) {
        m.destroy();
        return;
      }
      midiRef.current = m;
      m.selectDevice(prefs.midiSelectedDeviceId);
      setMidiDevices(m.listDevices());
      m.onDevicesChanged((devices) => setMidiDevices(devices));
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally only want to run this when the pref flips on or
    // the page mounts — not on every device-list change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.midiInputEnabled]);

  /**
   * MIDI-mode toggle handler. Same posture as vocal mode:
   *   - On: call MidiInput.init() inside the user gesture so the
   *     browser surfaces its permission prompt. On grant, populate the
   *     device list and persist the pref. On denial, revert.
   *   - Off: persist the pref and tear down the per-page MidiInput.
   */
  const handleMidiInputToggle = async (next: boolean) => {
    setMidiError(null);
    if (!next) {
      midiRef.current?.destroy();
      midiRef.current = null;
      setMidiDevices([]);
      await prefs.setMidiInputEnabled(false);
      return;
    }
    if (
      typeof navigator === "undefined" ||
      !(navigator as Navigator & { requestMIDIAccess?: unknown }).requestMIDIAccess
    ) {
      setMidiUnsupported(true);
      setMidiError(
        "This browser doesn't support the Web MIDI API. Try Chrome, Edge, or Opera, or a recent Safari/Firefox.",
      );
      return;
    }
    const m = createMidiInput();
    try {
      await m.init();
    } catch (err) {
      const name = (err as { name?: string }).name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        setMidiError(
          "MIDI access was blocked. Open your browser's site-permissions panel for this page and allow MIDI, then re-toggle this option.",
        );
      } else {
        setMidiError(
          `Couldn't access MIDI (${name ?? "unknown error"}). Make sure your device is plugged in and try again.`,
        );
      }
      m.destroy();
      // Make sure the pref stays off; revert any optimistic UI flip.
      await prefs.setMidiInputEnabled(false);
      return;
    }
    midiRef.current = m;
    m.selectDevice(prefs.midiSelectedDeviceId);
    setMidiDevices(m.listDevices());
    m.onDevicesChanged((devices) => setMidiDevices(devices));
    await prefs.setMidiInputEnabled(true);
  };

  const handleMidiDeviceSelect = async (id: string) => {
    // The "All devices" sentinel option uses the empty string — null
    // and `<option value={null}>` don't survive the round-trip.
    const next = id === "" ? null : id;
    midiRef.current?.selectDevice(next);
    await prefs.setMidiSelectedDeviceId(next);
  };

  // When the user arrives with vocal mode already enabled (persisted from
  // a previous session), look up the device label without re-prompting.
  // enumerateDevices returns labels only after permission was already
  // granted; if it returns "" we fall back to a generic placeholder.
  useEffect(() => {
    if (!prefs.vocalModeEnabled) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) return;
    let cancelled = false;
    void navigator.mediaDevices.enumerateDevices().then((devices) => {
      if (cancelled) return;
      const inputs = devices.filter((d) => d.kind === "audioinput");
      const first = inputs[0];
      if (!first) return;
      setMicLabel(first.label && first.label.length > 0 ? first.label : "Default microphone");
    });
    return () => {
      cancelled = true;
    };
  }, [prefs.vocalModeEnabled]);

  /* ─── Cloud sync (Block E) ───────────────────────────────────────── */
  const auth = useAuthStore();
  const sync = useSyncStore();
  const syncAvailable = isConfigured();

  const [emailInput, setEmailInput] = useState("");
  const [magicLinkStatus, setMagicLinkStatus] = useState<
    { kind: "idle" } | { kind: "sending" } | { kind: "sent"; email: string } | { kind: "error"; message: string }
  >({ kind: "idle" });

  const handleSendMagicLink = async () => {
    if (!emailInput.trim()) return;
    setMagicLinkStatus({ kind: "sending" });
    const { error } = await auth.signInWithEmail(emailInput.trim());
    if (error) {
      setMagicLinkStatus({ kind: "error", message: error });
    } else {
      setMagicLinkStatus({ kind: "sent", email: emailInput.trim() });
    }
  };

  const handleSignInWithGoogle = () => void auth.signInWithGoogle();

  const handleDeleteAccount = () => {
    setDeleteError(null);
    setDeleteConfirmPending(true);
  };

  const handleDeleteAccountConfirm = async () => {
    setDeleteConfirmPending(false);
    const { error } = await auth.deleteAccount();
    if (error) {
      setDeleteError(`Failed to delete account: ${error}`);
      return;
    }
    await clearAll();
    useCardsStore.getState().replaceAll({});
    location.reload();
  };

  const lastSyncLabel = (() => {
    if (sync.syncing) return "Syncing…";
    if (!sync.lastSyncAt) return "Never synced";
    const diffMs = Date.now() - new Date(sync.lastSyncAt).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "Just now";
    if (mins === 1) return "1 minute ago";
    if (mins < 60) return `${mins} minutes ago`;
    const hours = Math.floor(mins / 60);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  })();

  /* ─── FSRS optimizer (Feature 4 — per-track) ─────────────────────── */
  const OPTIMIZE_TRACKS: Array<{ value: "all" | Track; label: string }> = [
    { value: "all", label: "All tracks (global)" },
    { value: "pitch", label: "Pitch" },
    { value: "intervals", label: "Intervals" },
    { value: "harmony", label: "Harmony" },
    { value: "modes", label: "Modes" },
    { value: "dictation", label: "Dictation" },
  ];

  const [allReviews, setAllReviews] = useState<Awaited<ReturnType<typeof loadAllReviews>> | null>(null);
  const [optimizeTarget, setOptimizeTarget] = useState<"all" | Track>("all");
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState<{
    target: "all" | Track;
    weights: number[];
    baselineRmse: number;
    tunedRmse: number;
    trainingSize: number;
  } | null>(null);

  useEffect(() => {
    void loadAllReviews().then(setAllReviews);
  }, []);

  const reviewCount = allReviews?.length ?? null;
  const selectedTrackCount =
    optimizeTarget === "all"
      ? reviewCount
      : (allReviews?.filter((r) => getItemTrack(r.itemId) === optimizeTarget).length ?? null);
  const eligibilityThreshold = optimizeTarget === "all" ? 1000 : 300;
  const isEligible = (selectedTrackCount ?? 0) >= eligibilityThreshold;

  const handleOptimize = async () => {
    if (!allReviews) return;
    setOptimizing(true);
    setOptimizeResult(null);
    // Yield to event loop so the spinner renders before the heavy computation.
    await new Promise<void>((resolve) => setTimeout(resolve, 50));
    try {
      const filtered =
        optimizeTarget === "all"
          ? allReviews
          : allReviews.filter((r) => getItemTrack(r.itemId) === optimizeTarget);
      const samples = filtered.map((r) => ({
        itemId: r.itemId,
        timestamp: r.timestamp,
        rating: r.rating,
        correct: r.correct,
      }));
      const result = optimizeWeights(samples);
      setOptimizeResult({
        target: optimizeTarget,
        weights: result.weights,
        baselineRmse: result.baselineRmse,
        tunedRmse: result.rmse,
        trainingSize: result.trainingSize,
      });
    } finally {
      setOptimizing(false);
    }
  };

  const handleApplyResult = () => {
    if (!optimizeResult) return;
    if (optimizeResult.target === "all") {
      void prefs.setFsrsWeights(optimizeResult.weights);
    } else {
      const next = { ...(prefs.fsrsWeightsByTrack ?? {}), [optimizeResult.target]: optimizeResult.weights };
      void prefs.setFsrsWeightsByTrack(next);
    }
    setOptimizeResult(null);
  };

  const handleRevertTrack = (track: Track) => {
    const next = { ...(prefs.fsrsWeightsByTrack ?? {}) };
    delete next[track];
    void prefs.setFsrsWeightsByTrack(Object.keys(next).length > 0 ? next : null);
  };

  const handleRevertAll = () => {
    void prefs.setFsrsWeights(null);
    void prefs.setFsrsWeightsByTrack(null);
    setOptimizeResult(null);
  };

  const hasAnyCustomWeights =
    prefs.fsrsWeights !== null ||
    (prefs.fsrsWeightsByTrack !== null && Object.keys(prefs.fsrsWeightsByTrack).length > 0);
  const selectedTrackHasWeights =
    optimizeTarget !== "all" && (prefs.fsrsWeightsByTrack?.[optimizeTarget] ?? null) !== null;

  const handleResetPlacement = () => {
    setPlacementClearError(null);
    setPlacementConfirmPending(true);
  };

  const handleResetPlacementConfirm = async () => {
    setPlacementConfirmPending(false);
    const isSignedIn = !!auth.user;
    if (isSignedIn) {
      const { error } = await clearUserProgress();
      if (error) {
        setPlacementClearError(`Failed to clear cloud progress: ${error}`);
        return;
      }
    }
    await clearProgress();
    useCardsStore.getState().replaceAll({});
    // Bump the seed so the retake samples different items per node, and
    // unset the completion flag so the first-run flow re-prompts if the
    // user navigates away mid-placement.
    await prefs.setPlacementSeed(Date.now() & 0x7fffffff);
    await prefs.setPlacementCompleted(false);
    navigate("/placement");
  };

  // Guitar placement retake — re-seed only (no card clear), per design.
  const handleResetGuitarPlacement = async () => {
    await prefs.setGuitarPlacementSeed(Date.now() & 0x7fffffff);
    await prefs.setGuitarPlacementCompleted(false);
    navigate("/guitar/placement");
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="preferences"
        title="Settings"
        emphasis="Settings"
      />

      {syncAvailable && (
        <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-sync">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">cloud</p>
          <h2 id="section-sync" className="font-serif text-2xl font-medium text-ink mb-5">Sync across devices</h2>

          {auth.loading ? null : auth.user ? (
            /* ── Signed in ── */
            <div>
              <p className="text-sm text-inkSoft mb-1">
                Signed in as{" "}
                <span className="font-mono text-ink">{auth.user.email}</span>
              </p>
              <p className="text-xs text-inkFaded font-mono mb-4">{lastSyncLabel}</p>
              {sync.error && (
                <p role="alert" className="text-sm text-danger mb-4 font-serif italic">
                  Sync error: {sync.error}
                </p>
              )}
              <button
                type="button"
                onClick={() => void auth.signOut()}
                className={buttonClasses("secondary")}
              >
                Sign out
              </button>
              <div className="mt-6 pt-4 border-t border-ink/10">
                <p className="text-xs text-inkFaded font-mono mb-2">Danger zone</p>
                {deleteConfirmPending ? (
                  <div>
                    <p className="text-sm text-danger font-serif italic mb-2">
                      Permanently delete your account and all synced data? This also clears data on this device. This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void handleDeleteAccountConfirm()}
                        className="px-4 py-2 bg-danger text-paper hover:opacity-80 transition-colors text-sm"
                      >
                        Yes, delete my account
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmPending(false)}
                        className={buttonClasses("secondary")}
                      >
                        Cancel
                      </button>
                    </div>
                    {deleteError && (
                      <p role="alert" className="text-sm text-danger mt-2 font-serif italic">{deleteError}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      className="px-4 py-2 bg-transparent border border-danger/40 text-danger hover:bg-danger hover:text-paper transition-colors text-sm"
                    >
                      Delete account
                    </button>
                    <p className="text-xs text-inkFaded mt-2">
                      Permanently removes your account and all synced data. Local browser data is unaffected.
                    </p>
                    {deleteError && (
                      <p role="alert" className="text-sm text-danger mt-2 font-serif italic">{deleteError}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            /* ── Signed out ── */
            <div>
              <p className="text-sm text-inkSoft mb-5 font-serif italic">
                Sign in to back up your progress and use Earwise on multiple
                devices. We sync your card progress and preferences — nothing else.
                By signing in you agree to our{" "}
                <Link to="/terms" className="underline hover:text-accent">Terms</Link>
                {" "}and{" "}
                <Link to="/privacy" className="underline hover:text-accent">Privacy Policy</Link>.
              </p>

              {/* Magic-link */}
              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") void handleSendMagicLink(); }}
                  className="flex-1 px-3 py-2 border border-ink/20 bg-paper text-sm"
                  aria-label="Email address for sign in"
                  disabled={magicLinkStatus.kind === "sending" || magicLinkStatus.kind === "sent"}
                />
                <button
                  type="button"
                  onClick={() => void handleSendMagicLink()}
                  disabled={
                    !emailInput.trim() ||
                    magicLinkStatus.kind === "sending" ||
                    magicLinkStatus.kind === "sent"
                  }
                  className={buttonClasses("primary")}
                >
                  {magicLinkStatus.kind === "sending" ? "Sending…" : "Send magic link"}
                </button>
              </div>

              {magicLinkStatus.kind === "sent" && (
                <p className="text-sm text-success mb-3 font-serif italic">
                  Check your inbox — we sent a link to {magicLinkStatus.email}.
                </p>
              )}
              {magicLinkStatus.kind === "error" && (
                <p role="alert" className="text-sm text-danger mb-3 font-serif italic">
                  {magicLinkStatus.message}
                </p>
              )}

              {/* OAuth */}
              <p className="text-xs text-inkFaded font-mono mb-2">Or sign in with</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSignInWithGoogle}
                  className={buttonClasses("secondary")}
                >
                  Google
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-audio">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">sound</p>
        <h2 id="section-audio" className="font-serif text-2xl font-medium text-ink mb-5">Audio</h2>
        <Field label="Instrument">
          <select
            value={prefs.instrument}
            onChange={(e) => void prefs.setInstrument(e.target.value as typeof prefs.instrument)}
            className="w-full px-3 py-2 border border-ink/20 bg-paper"
          >
            <option value="synth">Synth (instant)</option>
            <option value="piano">Piano (Salamander Grand)</option>
            <option value="guitar">Guitar (placeholder)</option>
            <option value="voice">Voice (placeholder)</option>
          </select>
        </Field>
        <Field label="Confirmation tones after each answer">
          <input
            type="checkbox"
            checked={prefs.uiSoundsEnabled}
            onChange={(e) => void prefs.setUISoundsEnabled(e.target.checked)}
            className="accent-accent"
          />
        </Field>
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-vocal">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">input</p>
        <h2 id="section-vocal" className="font-serif text-2xl font-medium text-ink mb-5">Vocal mode</h2>
        <p className="text-sm text-inkSoft mb-4 font-serif italic">
          Sing the answer instead of clicking. Off by default. Used in
          scale-degree exercises today; interval grading is on the way.
          Earwise will only request microphone access when you flip
          this on.
        </p>
        <Field label="Use my voice for scale-degree exercises">
          <input
            type="checkbox"
            checked={prefs.vocalModeEnabled}
            onChange={(e) => void handleVocalModeToggle(e.target.checked)}
            className="accent-accent"
            aria-describedby="vocal-mode-help"
          />
        </Field>
        {prefs.vocalModeEnabled && micLabel && (
          <p
            id="vocal-mode-help"
            className="text-xs text-inkFaded font-mono mt-2"
          >
            Mic: {micLabel}
          </p>
        )}
        {vocalModeError && (
          <p
            id="vocal-mode-help"
            role="alert"
            className="text-sm text-danger mt-2 font-serif italic"
          >
            {vocalModeError}
          </p>
        )}
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-midi">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">input</p>
        <h2 id="section-midi" className="font-serif text-2xl font-medium text-ink mb-5">MIDI input</h2>
        <p className="text-sm text-inkSoft mb-4 font-serif italic">
          Use a connected MIDI keyboard to answer scale-degree, interval,
          chord-quality, and chord-inversion exercises. Always additive —
          the on-screen options stay clickable. Earwise will only request
          MIDI permission when you flip this on.
        </p>
        <Field label="Use a MIDI keyboard for supported exercises">
          <input
            type="checkbox"
            checked={prefs.midiInputEnabled}
            disabled={midiUnsupported}
            onChange={(e) => void handleMidiInputToggle(e.target.checked)}
            className="accent-accent"
            aria-describedby="midi-input-help"
          />
        </Field>
        {prefs.midiInputEnabled && !midiUnsupported && (
          <div className="mt-3" id="midi-input-help">
            <label className="block text-xs uppercase tracking-[0.18em] text-inkFaded font-mono mb-1">
              Device
            </label>
            <select
              value={prefs.midiSelectedDeviceId ?? ""}
              onChange={(e) => void handleMidiDeviceSelect(e.target.value)}
              className="w-full px-3 py-2 border border-ink/20 bg-paper"
              aria-label="MIDI device"
            >
              <option value="">All devices (default)</option>
              {midiDevices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.state === "disconnected" ? " (disconnected)" : ""}
                </option>
              ))}
            </select>
            {midiDevices.length === 0 ? (
              <p className="text-xs text-inkFaded font-mono mt-2">
                No MIDI devices detected. Plug one in and it will appear here.
              </p>
            ) : (
              <p className="text-xs text-inkFaded font-mono mt-2">
                {midiDevices.length} {midiDevices.length === 1 ? "device" : "devices"} detected.
              </p>
            )}
          </div>
        )}
        {midiError && (
          <p
            id="midi-input-help"
            role="alert"
            className="text-sm text-danger mt-2 font-serif italic"
          >
            {midiError}
          </p>
        )}
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-display">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">display</p>
        <h2 id="section-display" className="font-serif text-2xl font-medium text-ink mb-5">Display</h2>
        <Field label="Show letter names beneath solfège syllables">
          <input
            type="checkbox"
            checked={prefs.showLetterNames}
            onChange={(e) => void prefs.setShowLetterNames(e.target.checked)}
            className="accent-accent"
          />
        </Field>
        <Field label="Show next-review hint after each answer">
          <input
            type="checkbox"
            checked={prefs.showNextReviewHint}
            onChange={(e) => void prefs.setShowNextReviewHint(e.target.checked)}
            className="accent-accent"
          />
        </Field>
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-exercises">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">practice</p>
        <h2 id="section-exercises" className="font-serif text-2xl font-medium text-ink mb-5">Exercises</h2>
        <Field label="Ask for confidence after correct answers">
          <input
            type="checkbox"
            checked={prefs.confidenceSelfReport}
            onChange={(e) => void prefs.setConfidenceSelfReport(e.target.checked)}
            className="accent-accent"
          />
        </Field>
        <p className="text-xs text-inkFaded font-mono mt-1">
          After a correct answer, shows &quot;Got it / Wasn&apos;t sure&quot; for 2 s before advancing.
          &quot;Wasn&apos;t sure&quot; overrides the automatic FSRS rating to <em>hard</em>,
          helping the algorithm distinguish fast lucky guesses from genuine recall.
        </p>
        <div className="mt-4">
          <Field label="Auto-advance in listen mode">
            <input
              type="checkbox"
              checked={prefs.listenAutoAdvance}
              onChange={(e) => void prefs.setListenAutoAdvance(e.target.checked)}
              className="accent-accent"
            />
          </Field>
          <p className="text-xs text-inkFaded font-mono mt-1">
            In listen mode, automatically moves to the next exercise 3 seconds after the answer
            is revealed. Useful when your hands are busy. Can also be toggled directly on the
            listen screen.
          </p>
        </div>
        <div className="mt-4">
          <Field label="Guitar track (fretboard drills)">
            <input
              type="checkbox"
              checked={prefs.enabledInstrumentTracks.includes("guitar")}
              onChange={(e) => {
                const next: Track[] = e.target.checked
                  ? [...prefs.enabledInstrumentTracks.filter((t) => t !== "guitar"), "guitar"]
                  : prefs.enabledInstrumentTracks.filter((t) => t !== "guitar");
                void prefs.setEnabledInstrumentTracks(next);
              }}
              className="accent-accent"
              aria-describedby="guitar-track-help"
            />
          </Field>
          <p id="guitar-track-help" className="text-xs text-inkFaded font-mono mt-1">
            Adds the audio-first guitar track — hear a function in key, then tap it on the
            fretboard. On by default; it surfaces a Guitar hub (6 rungs: intervals,
            triad shapes, inversions, CAGED shapes, CAGED anchoring, diatonic triads) and folds
            guitar items into your plan. Turning it off re-hides them (your progress is kept).
          </p>
        </div>
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-training-plan">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">practice</p>
        <h2 id="section-training-plan" className="font-serif text-2xl font-medium text-ink mb-5">Training Plan</h2>
        <Field label="Active plan">
          <select
            value={prefs.activeSavedPlanId}
            onChange={(e) => void prefs.setActiveSavedPlanId(e.target.value)}
            className="w-full px-3 py-2 border border-ink/20 bg-paper"
            aria-describedby="training-plan-help"
          >
            {userPlans.length > 0 && (
              <optgroup label="My Plans">
                {userPlans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            )}
            <optgroup label="App Plans">
              {APP_PLANS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          </select>
        </Field>
        <p id="training-plan-help" className="text-xs text-inkFaded font-mono mt-2">
          {resolveActivePlan(prefs.activeSavedPlanId, userPlans).name}
        </p>
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-schedule">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">spaced repetition</p>
        <h2 id="section-schedule" className="font-serif text-2xl font-medium text-ink mb-5">Schedule</h2>

        <RetentionFieldset
          value={prefs.targetRetention}
          onChange={(v) => void prefs.setTargetRetention(v)}
          cards={cards}
        />

        <p className="text-sm text-inkSoft mb-4 font-serif italic">
          Tune the spaced-repetition algorithm to your review history.
          The optimizer fits the scheduling weights to how quickly you
          actually forget each skill. Per-track requires 300+ reviews on
          that track; global requires 1,000+.
        </p>

        <div className="mb-4">
          <label className="block text-xs font-mono uppercase tracking-[0.18em] text-inkFaded mb-2">
            Optimize for
          </label>
          <select
            value={optimizeTarget}
            onChange={(e) => { setOptimizeResult(null); setOptimizeTarget(e.target.value as "all" | Track); }}
            className="border border-ink/20 bg-paper text-ink text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {OPTIMIZE_TRACKS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {selectedTrackCount === null ? (
          <p className="text-xs text-inkFaded font-mono mb-4">Loading…</p>
        ) : (
          <p className="text-xs text-inkFaded font-mono mb-4">
            {selectedTrackCount.toLocaleString()} {selectedTrackCount === 1 ? "review" : "reviews"}{" "}
            {optimizeTarget === "all" ? "recorded" : `on ${optimizeTarget} track`}
            {!isEligible && (
              <span> — {(eligibilityThreshold - selectedTrackCount).toLocaleString()} more needed</span>
            )}
          </p>
        )}

        {!optimizeResult && (
          <>
            {prefs.fsrsWeights && optimizeTarget === "all" && (
              <p className="text-sm text-success font-serif italic mb-3">Using tuned global weights.</p>
            )}
            {optimizeTarget !== "all" && selectedTrackHasWeights && (
              <p className="text-sm text-success font-serif italic mb-3">
                Using tuned weights for {optimizeTarget}.
              </p>
            )}
          </>
        )}

        <div className="flex gap-3 flex-wrap mb-4">
          {isEligible && (
            <button
              type="button"
              onClick={() => void handleOptimize()}
              disabled={optimizing}
              className={buttonClasses("primary")}
            >
              {optimizing
                ? "Optimizing…"
                : optimizeTarget === "all"
                  ? "Tune my schedule"
                  : `Tune ${optimizeTarget} track`}
            </button>
          )}
          {!optimizeResult && selectedTrackHasWeights && (
            <button
              type="button"
              onClick={() => handleRevertTrack(optimizeTarget as Track)}
              className={buttonClasses("secondary")}
            >
              Revert this track
            </button>
          )}
          {!optimizeResult && hasAnyCustomWeights && (
            <button
              type="button"
              onClick={handleRevertAll}
              className={buttonClasses("secondary")}
            >
              Revert all to defaults
            </button>
          )}
        </div>

        {optimizeResult && (
          <div className="mt-2">
            <p className="text-sm text-inkSoft mb-1 font-serif italic">
              RMSE: {optimizeResult.baselineRmse.toFixed(4)} → {optimizeResult.tunedRmse.toFixed(4)}
              {optimizeResult.baselineRmse > 0 && (
                <span>
                  {" "}({((1 - optimizeResult.tunedRmse / optimizeResult.baselineRmse) * 100).toFixed(1)}% better)
                </span>
              )}
            </p>
            <p className="text-xs text-inkFaded font-mono mb-4">
              {optimizeResult.trainingSize.toLocaleString()} data points ·{" "}
              {optimizeResult.target === "all" ? "global" : `${optimizeResult.target} track`}
            </p>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleApplyResult}
                className={buttonClasses("primary")}
              >
                {optimizeResult.target === "all" ? "Apply tuned weights" : `Apply to ${optimizeResult.target} track`}
              </button>
              <button
                type="button"
                onClick={() => setOptimizeResult(null)}
                className={buttonClasses("secondary")}
              >
                Discard
              </button>
              {hasAnyCustomWeights && (
                <button
                  type="button"
                  onClick={handleRevertAll}
                  className={buttonClasses("secondary")}
                >
                  Revert all to defaults
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-curriculum">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">curriculum</p>
        <h2 id="section-curriculum" className="font-serif text-2xl font-medium text-ink mb-5">Curriculum</h2>
        <Field label="Include leading tone (ti) in scale-degree exercises">
          <input
            type="checkbox"
            checked={prefs.includeLeadingTone}
            onChange={(e) => void prefs.setIncludeLeadingTone(e.target.checked)}
            className="accent-accent"
          />
        </Field>
        <Field label="Mastery thresholds">
          <select
            value={prefs.masteryPreset}
            onChange={(e) => void prefs.setMasteryPreset(e.target.value as keyof typeof MASTERY_PRESETS)}
            className="w-full px-3 py-2 border border-ink/20 bg-paper"
            aria-describedby="mastery-preset-help"
          >
            <option value="standard">Standard — 30 / 7 days</option>
            <option value="intensive">Intensive — 14 / 5 days</option>
            <option value="accelerated">Accelerated — 7 / 3 days</option>
          </select>
        </Field>
        <Field label="Cadence pace">
          <select
            value={prefs.cadenceTempo}
            onChange={(e) => void prefs.setCadenceTempo(e.target.value as "slow" | "medium" | "fast")}
            className="w-full px-3 py-2 border border-ink/20 bg-paper"
            aria-describedby="cadence-tempo-help"
          >
            <option value="slow">Slow — comfortable (~3.7 s)</option>
            <option value="medium">Medium — brisk (~2.6 s)</option>
            <option value="fast">Fast — snappy (~1.8 s)</option>
          </select>
        </Field>
        <p id="cadence-tempo-help" className="text-xs text-inkFaded font-mono mt-2">
          Speed of the I–IV–V–I cadence played before each exercise to set key context. The Cadence drill has its own tempo control for the cadence itself.
        </p>
        <p id="mastery-preset-help" className="text-xs text-inkFaded font-mono mt-2">
          Mastered / Practicing stability thresholds. Affects Skill Tree, Stats,
          and the focus-item pool — not the review schedule itself.
        </p>
      </section>

      {/*
        Testing section visibility:
          - Always visible in dev builds.
          - In production, visible only after the user has unlocked via
            the URL-hash trigger (so they can also turn it off again).
            Untriggered prod users never see this section.
      */}
      {(import.meta.env.DEV || prefs.unlockEverything) && (
        <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-testing">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">developer</p>
          <h2 id="section-testing" className="font-serif text-2xl font-medium text-ink mb-5">Testing</h2>
          <p className="text-sm text-inkSoft mb-3 font-serif italic">
            Preview every node without finishing prerequisites. Card progress is
            unaffected — only what&apos;s <em>visible</em> changes.
          </p>
          <Field label="Unlock all curriculum (bypass prereqs)">
            <input
              type="checkbox"
              checked={prefs.unlockEverything}
              onChange={(e) => void prefs.setUnlockEverything(e.target.checked)}
              className="accent-accent"
              aria-describedby="unlock-everything-help"
            />
          </Field>
          <p
            id="unlock-everything-help"
            className="text-xs text-inkFaded font-mono mt-2"
          >
            Hidden in production unless arrived via the unlock URL.
          </p>
        </section>
      )}

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-placement">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">diagnostic</p>
        <h2 id="section-placement" className="font-serif text-2xl font-medium text-ink mb-5">Placement</h2>
        <p className="text-sm text-inkSoft mb-4 font-serif italic">
          Retake the placement test if you want to re-baseline what the
          scheduler thinks you know. Card progress is cleared; preferences
          are preserved.
        </p>
        {placementConfirmPending ? (
          <div>
            <p className="text-sm text-ink font-serif italic mb-2">
              {auth.user
                ? "This clears your card progress on this device and in the cloud (all synced devices), then takes you back to the placement test. Your preferences and drill history are preserved."
                : "This clears your card progress and takes you back to the placement test. Your preferences are preserved."}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleResetPlacementConfirm()}
                className={buttonClasses("primary")}
              >
                Yes, reset placement
              </button>
              <button
                type="button"
                onClick={() => setPlacementConfirmPending(false)}
                className={buttonClasses("secondary")}
              >
                Cancel
              </button>
            </div>
            {placementClearError && (
              <p role="alert" className="text-sm text-danger mt-2 font-serif italic">{placementClearError}</p>
            )}
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={handleResetPlacement}
              className={buttonClasses("secondary")}
            >
              Reset placement
            </button>
            {placementClearError && (
              <p role="alert" className="text-sm text-danger mt-2 font-serif italic">{placementClearError}</p>
            )}
          </>
        )}
        {prefs.enabledInstrumentTracks.includes("guitar") && (
          <div className="mt-6 pt-5 border-t border-ink/10">
            <p className="text-sm text-inkSoft mb-3 font-serif italic">
              Retake the <strong className="text-ink not-italic font-medium">guitar</strong> placement
              to re-baseline the fretboard curriculum. Guitar cards are re-seeded from
              your answers; ear-training progress is untouched.
            </p>
            <button
              type="button"
              onClick={() => void handleResetGuitarPlacement()}
              className={buttonClasses("secondary")}
            >
              Reset guitar placement
            </button>
          </div>
        )}
      </section>

      <section className="border border-ink/10 p-6 mb-6" aria-labelledby="section-data">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1">backup</p>
        <h2 id="section-data" className="font-serif text-2xl font-medium text-ink mb-5">Data</h2>
        <p className="text-sm text-inkSoft mb-4 font-serif italic">
          All your progress lives in this browser. Export to back up or move to another device.
        </p>
        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => void handleExport()}
            className={buttonClasses("primary")}
          >
            Export data (JSON)
          </button>
          <button
            type="button"
            onClick={() => importInputRef.current?.click()}
            className={buttonClasses("secondary")}
          >
            Import data
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".json"
            onChange={(e) => void handleImportFile(e)}
            className="sr-only"
            aria-label="Import data file"
          />
          {!clearConfirmPending && (
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 bg-transparent border border-danger/40 text-danger hover:bg-danger hover:text-paper transition-colors"
            >
              Clear all data
            </button>
          )}
        </div>
        {pendingImportPayload && (
          <div className="mt-3 p-3 border border-ink/20 bg-paperWarm">
            <p className="text-sm text-ink font-serif italic mb-2">
              You are signed in. Imported data will be pushed to the cloud on the next sync.
              If your cloud already has newer records, those will win. To do a clean restore,
              use &ldquo;Delete all data&rdquo; first, then import.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void doImport(pendingImportPayload)}
                className={buttonClasses("primary")}
              >
                Import anyway
              </button>
              <button
                type="button"
                onClick={() => { setPendingImportPayload(null); setImportStatus(null); }}
                className={buttonClasses("secondary")}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {importStatus?.kind === "success" && (
          <p role="status" className="text-sm text-success font-serif italic mt-2">Import successful.</p>
        )}
        {importStatus?.kind === "error" && (
          <p role="alert" className="text-sm text-danger font-serif italic mt-2">Import failed: {importStatus.message}</p>
        )}
        {clearConfirmPending && (
          <div className="mt-3 p-3 border border-danger/30 bg-paper">
            <p className="text-sm text-danger font-serif italic mb-2">
              {auth.user
                ? "This deletes all your progress on this device and in the cloud (all synced devices). Your account stays active."
                : "This deletes all your progress on this device."}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleClearConfirm()}
                className="px-4 py-2 bg-danger text-paper hover:opacity-80 transition-colors text-sm"
              >
                Yes, delete all data
              </button>
              <button
                type="button"
                onClick={() => setClearConfirmPending(false)}
                className={buttonClasses("secondary")}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {clearError && (
          <p role="alert" className="text-sm text-danger mt-2 font-serif italic">{clearError}</p>
        )}
      </section>
      <footer className="pt-6 border-t border-ink/10 flex gap-4">
        <Link to="/privacy" className="text-xs text-inkFaded hover:text-ink underline font-mono">
          Privacy Policy
        </Link>
        <Link to="/terms" className="text-xs text-inkFaded hover:text-ink underline font-mono">
          Terms of Service
        </Link>
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className="flex items-center justify-between gap-4 py-3 border-b border-ink/10">
      <span className="text-sm text-inkSoft">{label}</span>
      <div className="min-w-[160px]">{children}</div>
    </label>
  );
}

const RETENTION_OPTIONS: { value: number; label: string; caption: string }[] = [
  { value: 0.80, label: "80%", caption: "fewer reviews, some forgetting" },
  { value: 0.85, label: "85%", caption: "" },
  { value: 0.90, label: "90%", caption: "recommended" },
  { value: 0.95, label: "95%", caption: "more reviews, near-perfect retention" },
];

function RetentionFieldset({
  value,
  onChange,
  cards,
}: {
  value: number;
  onChange: (v: number) => void;
  cards: CardStore;
}): ReactNode {
  const avgPerDay = (() => {
    const counts = forecastDue(cards as Parameters<typeof forecastDue>[0], 7);
    const total = counts.reduce((s, n) => s + n, 0);
    return Math.round(total / 7);
  })();

  return (
    <fieldset className="mb-6 border border-ink/10 p-4">
      <legend className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded px-1 mb-3">
        Target retention
      </legend>
      <div className="space-y-2 mb-4">
        {RETENTION_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="targetRetention"
              value={opt.value}
              checked={Math.abs(value - opt.value) < 0.001}
              onChange={() => onChange(opt.value)}
              className="accent-accent"
            />
            <span className="font-mono text-sm text-ink">{opt.label}</span>
            {opt.caption && (
              <span className="text-xs text-inkSoft font-serif italic">— {opt.caption}</span>
            )}
          </label>
        ))}
      </div>
      <p className="text-xs text-inkFaded font-mono mb-3">
        Next 7 days average: ~{avgPerDay} reviews/day at current card state.
      </p>
      <p className="text-xs text-inkSoft font-serif italic">
        Changing target retention affects new intervals only. Cards already scheduled are
        not rescheduled; the new target takes full effect over your next few weeks of practice.
      </p>
    </fieldset>
  );
}
